import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm, useWatch } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import DrawerWrapper from '../../../components/DrawerWrapper/DrawerWrapper';
import createFormTextField from '../../../components/FormTextFields/FormTextFields';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useUsers } from '../../../contexts/userContext';
import { IApiError } from '../../../services/types';
import useYupValidationResolver from '../../../utils/formUtils/yupValidationResolver';
import defaultWorkWeekConfig from '../../../config/workWeekConfig';
import { useWorkWeekConfigQuery } from '../../Scheduling/requests/queries';
import { WorkWeekConfigResponse } from '../../Scheduling/types';
import SchedulingUtilities from '../../utilities/schedulingUtilities';
import { TIME_FORMAT_REGEX, TIME_PLACEHOLDER } from '../../utilities/constants';
import { useCreateTipShareShiftMutation, useUpdateTipShareShiftMutation } from '../../requests/mutations';
import { CreateTipShareShiftDto, TipShareShift } from '../../types';
import { drawerContentBoxSx, drawerTitleSx } from '../styles';

interface CreateEditShiftDrawerProps {
  open: boolean;
  onClose: () => void;
  shift: TipShareShift | undefined;
  existingShifts?: TipShareShift[];
  isEditing: boolean;
  readOnly?: boolean;
  onEnterEditMode?: () => void;
}

const findOverlappingShift = (
  startTime: string,
  endTime: string,
  existingShifts: TipShareShift[],
  currentShiftId?: string
): TipShareShift | undefined => {
  const comparableShifts = existingShifts.filter((existingShift) => existingShift.id !== currentShiftId);

  if (comparableShifts.length === 0) {
    return undefined;
  }

  const currentShiftCoverage = SchedulingUtilities.getCoveredMinutes(startTime, endTime);
  const currentShiftCoverageMinutes = Array.from(currentShiftCoverage);

  return comparableShifts.find((existingShift) => {
    const existingShiftCoverage = SchedulingUtilities.getCoveredMinutes(existingShift.startTime, existingShift.endTime);

    return currentShiftCoverageMinutes.some((minute) => existingShiftCoverage.has(minute));
  });
};

const buildConflictMessage = (
  t: (key: string, options?: Record<string, string>) => string,
  conflictingShift: TipShareShift
): string => {
  const formattedTimeRange = `${SchedulingUtilities.formatTime24To12(
    conflictingShift.startTime
  )} - ${SchedulingUtilities.formatTime24To12(conflictingShift.endTime)}`;

  if (conflictingShift.name) {
    return t('labor.tipShareShifts.messages.conflictWithNamedShift', {
      shiftName: conflictingShift.name,
      formattedTimeRange,
    });
  }

  return t('labor.tipShareShifts.messages.conflictWithExistingShift', {
    formattedTimeRange,
  });
};

const normalizeShiftName = (name: string): string => name.trim().toLowerCase();

const findDuplicateShiftName = (
  name: string,
  existingShifts: TipShareShift[],
  currentShiftId?: string
): TipShareShift | undefined => {
  const normalizedName = normalizeShiftName(name);

  if (!normalizedName) {
    return undefined;
  }

  return existingShifts.find(
    (existingShift) => existingShift.id !== currentShiftId && normalizeShiftName(existingShift.name) === normalizedName
  );
};

const getValidationSchema = (t: (key: string) => string, workdayStartTime: string) =>
  yup.object({
    name: yup.string().trim().required(t('formMessage.required')).max(50),
    description: yup.string().optional().max(500),
    startTime: yup
      .string()
      .required(t('formMessage.required'))
      .matches(TIME_FORMAT_REGEX, t('labor.tipShareShifts.validation.invalidTimeFormat')),
    endTime: yup
      .string()
      .required(t('formMessage.required'))
      .matches(TIME_FORMAT_REGEX, t('labor.tipShareShifts.validation.invalidTimeFormat'))
      .test('occursWithinWorkday', t('formMessage.beforeWorkDayStartTime'), (value, { parent }) => {
        if (!parent.startTime || !value) {
          return true;
        }

        return SchedulingUtilities.clockInTimeInBusinessHours(
          SchedulingUtilities.normalizeTimeString(parent.startTime),
          SchedulingUtilities.normalizeTimeString(value),
          SchedulingUtilities.normalizeTimeString(workdayStartTime)
        );
      }),
  });

function CreateEditShiftDrawer({
  open,
  onClose,
  shift,
  existingShifts = [],
  isEditing = false,
  readOnly = false,
  onEnterEditMode,
}: Readonly<CreateEditShiftDrawerProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { setSnackbarState } = useSnackbar();
  const user = useUsers();
  const isUserAuthenticated = user.fullyAuthenticated === 'authenticated';

  const createMutation = useCreateTipShareShiftMutation();
  const updateMutation = useUpdateTipShareShiftMutation();
  const { data: workWeekConfigData } = useWorkWeekConfigQuery(isUserAuthenticated);
  const workWeekData = workWeekConfigData as WorkWeekConfigResponse | undefined;
  const workdayStartTime = SchedulingUtilities.normalizeTimeString(
    workWeekData?.workdayStartTime ?? defaultWorkWeekConfig.workdayStartTime
  );

  const validationSchema = useMemo(() => getValidationSchema(t, workdayStartTime), [t, workdayStartTime]);
  const resolver = useYupValidationResolver(validationSchema);

  const defaultValues = useMemo<CreateTipShareShiftDto>(
    () => ({
      name: shift?.name ?? '',
      description: shift?.description ?? '',
      startTime: readOnly ? SchedulingUtilities.formatTime24To12(shift?.startTime) : shift?.startTime ?? '',
      endTime: readOnly ? SchedulingUtilities.formatTime24To12(shift?.endTime) : shift?.endTime ?? '',
    }),
    [readOnly, shift]
  );

  const formContext = useForm<CreateTipShareShiftDto>({
    resolver,
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { formState, reset, control } = formContext;
  const { isSubmitting, isDirty } = formState;
  const [shiftConflictErrorMessage, setShiftConflictErrorMessage] = useState('');
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const watchedName = useWatch({ control, name: 'name' });
  const watchedStartTime = useWatch({ control, name: 'startTime' });
  const watchedEndTime = useWatch({ control, name: 'endTime' });

  // Reset form when drawer opens with shift data or when shift changes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setShiftConflictErrorMessage('');
    }
  }, [open, defaultValues, reset]);

  useEffect(() => {
    if (shiftConflictErrorMessage) {
      scrollableRef.current?.scrollIntoView?.();
    }
  }, [shiftConflictErrorMessage]);

  useEffect(() => {
    setShiftConflictErrorMessage((currentMessage) => (currentMessage ? '' : currentMessage));
  }, [watchedName, watchedStartTime, watchedEndTime]);

  const handleClose = useCallback(() => {
    setShiftConflictErrorMessage('');
    onClose();
  }, [onClose]);

  const getDrawerTitle = useCallback(() => {
    if (readOnly && shift) return shift.name;
    if (isEditing) return t('labor.tipShareShifts.dialog.editTitle');
    return t('labor.tipShareShifts.dialog.createTitle');
  }, [readOnly, shift, isEditing, t]);

  const onSubmit = useCallback(
    async (data: CreateTipShareShiftDto) => {
      try {
        const normalizedData = {
          ...data,
          name: data.name.trim(),
          startTime: SchedulingUtilities.normalizeTimeString(data.startTime),
          endTime: SchedulingUtilities.normalizeTimeString(data.endTime),
        };

        const isUpdating = isEditing && !isNil(shift);
        const duplicateShift = findDuplicateShiftName(
          normalizedData.name,
          existingShifts,
          isUpdating ? shift.id : undefined
        );

        if (duplicateShift) {
          setShiftConflictErrorMessage(t('labor.tipShareShifts.messages.duplicateName'));
          return;
        }

        const conflictingShift = findOverlappingShift(
          normalizedData.startTime,
          normalizedData.endTime,
          existingShifts,
          isUpdating ? shift.id : undefined
        );

        if (conflictingShift) {
          setShiftConflictErrorMessage(buildConflictMessage(t, conflictingShift));
          return;
        }

        if (isUpdating) {
          await updateMutation.mutateAsync({ shiftId: shift.id, updateDto: normalizedData });
        } else {
          await createMutation.mutateAsync(normalizedData);
        }

        setShiftConflictErrorMessage('');
        setSnackbarState({
          open: true,
          message: t('labor.tipShareShifts.messages.success', { eventType: isUpdating ? 'updated' : 'created' }),
          color: 'success',
        });
        handleClose();
      } catch (error: unknown) {
        const apiError = error as IApiError;

        const errorMessage =
          apiError?.details?.message ?? (apiError?.message || t('labor.tipShareShifts.messages.error'));
        setSnackbarState({
          open: true,
          message: errorMessage,
          color: 'error',
        });
      }
    },
    [isEditing, shift, existingShifts, updateMutation, createMutation, setSnackbarState, t, handleClose]
  );

  const formFields: FieldValues[] = useMemo(
    () => [
      {
        name: 'name',
        variant: 'text',
        required: true,
        placeholder: t('labor.tipShareShifts.fields.namePlaceholder'),
        label: t('labor.tipShareShifts.fields.name'),
        readOnly,
        disableUnderline: readOnly,
      },
      {
        name: 'description',
        variant: 'text',
        required: false,
        placeholder: t('labor.tipShareShifts.fields.descriptionPlaceholder'),
        label: t('labor.tipShareShifts.fields.description'),
        multiline: true,
        minRows: 2,
        readOnly,
        disableUnderline: readOnly,
      },
      {
        name: 'startTime',
        variant: readOnly ? 'standard' : 'time',
        required: true,
        placeholder: readOnly ? undefined : TIME_PLACEHOLDER,
        label: t('labor.tipShareShifts.columns.startTime'),
        dataTestId: 'startTime',
        inputType: readOnly ? 'text' : undefined,
        lightMode: theme.palette.mode === 'light',
        isInputComplete: Boolean(watchedStartTime),
        readOnly,
        disableUnderline: readOnly,
      },
      {
        name: 'endTime',
        variant: readOnly ? 'standard' : 'time',
        required: true,
        placeholder: readOnly ? undefined : TIME_PLACEHOLDER,
        label: t('labor.tipShareShifts.columns.endTime'),
        dataTestId: 'endTime',
        inputType: readOnly ? 'text' : undefined,
        lightMode: theme.palette.mode === 'light',
        isInputComplete: Boolean(watchedEndTime),
        readOnly,
        disableUnderline: readOnly,
      },
    ],
    [t, theme.palette.mode, readOnly, watchedStartTime, watchedEndTime]
  );

  return (
    <FormContainer onSuccess={onSubmit} formContext={formContext}>
      <DrawerWrapper
        open={open}
        closeDrawer={handleClose}
        hasSaveFooter={!readOnly}
        useProcessingFlow={!readOnly}
        isProcessing={isSubmitting}
        disabled={isEditing && !isDirty}
        data-testid='create-edit-shift-drawer'
        headerActionButtonText={readOnly ? t('labor.tipShareShifts.actions.edit') : undefined}
        onHeaderActionClick={readOnly ? onEnterEditMode : undefined}
        headerActionButtonType='outlined'
      >
        <Box ref={scrollableRef} sx={drawerContentBoxSx}>
          <Typography variant='h6' sx={drawerTitleSx}>
            {getDrawerTitle()}
          </Typography>

          {shiftConflictErrorMessage && (
            <Alert severity='error' data-testid='shift-error-alert'>
              {shiftConflictErrorMessage}
            </Alert>
          )}
          {formFields.map((field) => createFormTextField(field, { t, tKey: 'labor' }))}
        </Box>
      </DrawerWrapper>
    </FormContainer>
  );
}

CreateEditShiftDrawer.defaultProps = {
  existingShifts: [],
  onEnterEditMode: undefined,
  readOnly: false,
};

export default CreateEditShiftDrawer;
